"use client"

import { useEffect, useState, useCallback } from "react"
import Link from "next/link"
import Image from "next/image"
import { useTheme } from "@/lib/theme"
import { Button } from "@/components/ui/button"
import { UserRound } from "lucide-react"
import { useRouter, usePathname } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { useBannerOffset } from "@/lib/contexts/NotificationBannerContext"
import { useHeaderHeight } from "@/lib/contexts/HeaderHeightContext"
import { useAuthContext } from "@/lib/auth/context/AuthContext"
import { PromoBanner } from "@/components/promo-banner"
import { trackNavigationClicked } from "@/lib/posthog-events"
import { useFeatureFlag } from "@/lib/hooks/useFeatureFlag"

export function Header() {
  const bannerOffset = useBannerOffset()
  const { headerRef } = useHeaderHeight()
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [firstName, setFirstName] = useState<string | null>(null)
  const router = useRouter()
  const pathname = usePathname()

  // FIX: Use AuthContext instead of creating duplicate subscription
  // This prevents multiple onAuthStateChange subscriptions that cause token refresh storms
  const { user } = useAuthContext()

  // Fetch user profile data when user changes
  const fetchUserData = useCallback(async () => {
    if (!user) {
      setAvatarUrl(null)
      setFirstName(null)
      return
    }

    const supabase = createClient()
    const { data: profileData } = await supabase
      .from("profiles")
      .select("avatar_url, first_name")
      .eq("id", user.id)
      .single()

    setAvatarUrl(profileData?.avatar_url || user.user_metadata?.avatar_url || null)
    setFirstName(profileData?.first_name || user.user_metadata?.first_name || null)
  }, [user])

  useEffect(() => {
    setMounted(true)
  }, [])

  // Fetch user data whenever user changes
  useEffect(() => {
    fetchUserData()
  }, [fetchUserData])

  const handleProfileClick = () => {
    trackNavigationClicked(user ? "profile" : "sign_in", pathname)
    if (user) {
      // Check if this is an OAuth user (auto-onboarded)
      const provider = user?.app_metadata?.provider
      const providers = user?.app_metadata?.providers as string[] | undefined
      const identities = user?.identities
      const oauthProviders = ['google', 'facebook', 'github', 'apple', 'azure', 'twitter']

      const isOAuthFromProvider = provider && oauthProviders.includes(provider)
      const isOAuthFromProviders = providers?.some(p => oauthProviders.includes(p))
      const isOAuthFromIdentities = identities?.some(i => oauthProviders.includes(i.provider))
      const isOAuthUser = isOAuthFromProvider || isOAuthFromProviders || isOAuthFromIdentities

      // OAuth users are auto-onboarded, go straight to dashboard
      if (isOAuthUser) {
        router.push("/dashboard")
        return
      }

      const hasCompletedOnboarding = user?.user_metadata?.onboarding_completed
      const hasName = user?.user_metadata?.first_name ||
                      user?.user_metadata?.full_name ||
                      user?.user_metadata?.name

      if (hasCompletedOnboarding || hasName) {
        router.push("/dashboard")
      } else {
        router.push("/auth/onboarding")
      }
    } else {
      router.push("/sign-in")
    }
  }

  const renderProfileContent = () => {
    if (!mounted) {
      return <UserRound className="w-5 h-5" />
    }

    if (user && avatarUrl) {
      return (
        <img
          src={avatarUrl || "/placeholder.svg"}
          alt="User Avatar"
          className="w-full h-full rounded-full object-cover"
        />
      )
    }

    if (user) {
      const initial = firstName?.charAt(0) || user.email?.charAt(0) || "U"
      return <span className="font-bold text-lg">{initial.toUpperCase()}</span>
    }

    return <UserRound className="w-5 h-5" />
  }

  // Determine which logo to show based on the current route
  const isRateCenterAuth = pathname?.startsWith('/auth/rate-center') || pathname?.startsWith('/rate')
  const logoSrc = isRateCenterAuth ? "/logo-rate-center.svg" : "/goldcenter-logo.svg"
  const logoAlt = isRateCenterAuth ? "Rate Center Logo" : "Gold Center Logo"
  // Use Rate Center homepage logo sizing (38.4px) when on Rate Center pages
  const logoHeight = isRateCenterAuth ? 38.4 : 32
  const logoClass = isRateCenterAuth ? "w-auto" : "h-8 w-auto"

  // Home path is always "/" - middleware handles domain-specific rewrites
  const homePath = '/'
  const isAlreadyHome = pathname === homePath
  // gf-go-0031: undefined (flag still loading) must behave as OFF, never ON —
  // the logo is a real <a href>, so a truthy-by-accident flag would swallow a
  // genuine navigation click during the load window.
  const rageclickFix = useFeatureFlag('gf-go-0031') === true

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackNavigationClicked("logo_home", pathname)
    if (rageclickFix && isAlreadyHome) {
      e.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <div
      ref={headerRef}
      className="fixed w-full z-[100] transition-[top] duration-300"
      style={{ top: `${bannerOffset}px` }}
    >
      <PromoBanner />
      <header
        className="bg-[#01323E] w-full shadow-lg shadow-gray-400/65 dark:shadow-none"
      >
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <div className="flex-1"></div>

        <div className="flex-grow-0 flex justify-center items-center">
          <Link
            href={homePath}
            onClick={handleLogoClick}
            className="cursor-pointer flex items-center gap-2 min-h-[44px] px-4 py-2"
          >
            <Image
              src={logoSrc}
              alt={logoAlt}
              width={isRateCenterAuth ? 0 : 32}
              height={logoHeight}
              priority
              className={logoClass}
              style={isRateCenterAuth ? { height: '38.4px', width: 'auto', maxWidth: 'none' } : undefined}
            />
          </Link>
        </div>

        <div className="flex items-center -space-x-1 flex-1 justify-end">
          <Button
            onClick={handleProfileClick}
            variant="ghost"
            size="icon"
            className="text-white w-9 h-9 bg-transparent border-none outline-none shadow-none hover:bg-transparent focus:bg-transparent active:bg-transparent"
            aria-label="Go to profile"
          >
            {renderProfileContent()}
          </Button>
        </div>
      </div>
    </header>
    </div>
  )
}
